import { Loader } from "@mantine/core";
import { Play, Timer } from "lucide-react";
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";

import classes from "./VideoScrubber.module.css";

/** Live's scrubber's numbers (`video-scrubber`, read for behaviour). */
const IDLE_PLAY_MS = 900;
const STEP_S = 0.5;
const BUFFER_SHOWN_MS = 100;
// A seek can land just short of its time, which would read a second early: it aims a little past,
// and the shown time is padded before it is floored.
const SEEK_PAST_S = 0.05;
const SHOWN_PAST_S = 0.15;

/**
 * Live's time on a card: "00:23", "01:41", "01:02:03", seconds rounded up. Rounded before it is
 * split, so 59.4 s reads "01:00" (live's splits first and reads "00:60").
 */
export function scrubTime(seconds: number): string {
  const total = Math.ceil(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  const two = (value: number) => String(value).padStart(2, "0");
  return hours > 0 ? `${two(hours)}:${two(minutes)}:${two(rest)}` : `${two(minutes)}:${two(rest)}`;
}

export interface VideoScrubberProps {
  /** The video's signed URL. */
  src: string;
  /** Its still: a thumbnail, a poster, or a search hit's scene frame; none shows the video's frame. */
  poster: string | null;
  /** Its length when the server knows it; else the file's, once read. */
  durationMs: number | null;
  /** Where the card rests (a search hit's scene frame): shown, and gone back to on leave. */
  thumbnailSeconds?: number | null;
  /** Names the picture for a screen reader: the file's name. */
  label: string;
  /** Shown instead once the video cannot be read: the card's placeholder. */
  fallback: ReactNode;
  /** The video could not be read (its signed URL may have expired): the card's owner reads it
   * again, once (G5's client rule, as `OutputMedia`): a fresh URL gets one more try, and a second
   * failure shows the fallback for good. */
  onFailed?: () => void;
}

/** With no still, the frame is taken a moment in, since some browsers paint nothing at 0. */
const FRAME_AT = "#t=0.1";

/**
 * Live's video on a library card (`video-scrubber`): the pointer's place across it picks the time,
 * by half seconds, with a thin line where it is; held still for 0.9 s it plays, muted, on a loop;
 * leaving pauses it, back at `thumbnailSeconds` when there is one. The time reads "now / length"
 * at the bottom right: the rest time and the length at rest when it has one, else the length,
 * and while hovered the time under the pointer.
 */
export function VideoScrubber(props: VideoScrubberProps) {
  const { src, poster, durationMs, thumbnailSeconds = null, label, fallback, onFailed } = props;
  const video = useRef<HTMLVideoElement>(null);
  const idle = useRef<number | null>(null);
  const bufferShown = useRef<number | null>(null);
  const lastSeek = useRef<number | null>(null);
  const hovering = useRef(false);
  const restedAt = useRef(false);
  const [over, setOver] = useState(false);
  const [lineAt, setLineAt] = useState<number | null>(null);
  const [now, setNow] = useState<number | null>(null);
  const [buffering, setBuffering] = useState(false);
  const [readS, setReadS] = useState<number | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  // Read again once a card: a broken video, not an expired URL, would ask at every fresh URL.
  const readAgain = useRef(false);
  const lengthS = durationMs !== null && durationMs > 0 ? durationMs / 1000 : readS;

  const stopTimers = () => {
    if (idle.current !== null) window.clearTimeout(idle.current);
    if (bufferShown.current !== null) window.clearTimeout(bufferShown.current);
    idle.current = null;
    bufferShown.current = null;
  };
  useEffect(() => stopTimers, []);

  const seekTo = (seconds: number) => {
    const element = video.current;
    if (element === null) return;
    const stepped = Math.round(seconds / STEP_S) * STEP_S;
    if (stepped === lastSeek.current) return;
    lastSeek.current = stepped;
    element.currentTime = stepped + SEEK_PAST_S;
  };
  const scrubAt = (event: MouseEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    setLineAt(ratio);
    if (lengthS !== null) seekTo(ratio * lengthS);
  };
  const playWhenStill = () => {
    if (idle.current !== null) window.clearTimeout(idle.current);
    idle.current = window.setTimeout(() => {
      idle.current = null;
      const element = video.current;
      if (!hovering.current || element === null) return;
      // A refused play (autoplay rules) keeps the frame.
      void Promise.resolve(element.play()).catch(() => undefined);
      setLineAt(null);
    }, IDLE_PLAY_MS);
  };
  const backToRest = () => {
    const element = video.current;
    if (element === null || thumbnailSeconds === null) return;
    const length = Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null;
    if (length !== null) element.currentTime = Math.min(length, thumbnailSeconds + SEEK_PAST_S);
  };

  // At rest, the shown time; hovered, the time under the pointer once the video reports it.
  const shown = over && now !== null ? now : thumbnailSeconds;
  const time =
    thumbnailSeconds !== null
      ? { at: shown, length: lengthS }
      : over && now !== null
        ? { at: now, length: null }
        : { at: null, length: lengthS };
  const Icon = time.at !== null ? Timer : time.length === null ? Play : null;

  // Keyed by the URL, so a fresh URL after a failure gets a fresh try.
  if (failedSrc === src) return fallback;
  return (
    <div
      className={classes.scrubber}
      onMouseEnter={(event) => {
        hovering.current = true;
        setOver(true);
        const element = video.current;
        if (element !== null) {
          element.preload = "auto";
          element.pause();
        }
        scrubAt(event);
        playWhenStill();
      }}
      onMouseMove={(event) => {
        const element = video.current;
        if (element !== null && !element.paused) element.pause();
        scrubAt(event);
        playWhenStill();
      }}
      onMouseLeave={() => {
        hovering.current = false;
        setOver(false);
        setLineAt(null);
        setBuffering(false);
        setNow(null);
        stopTimers();
        lastSeek.current = null;
        video.current?.pause();
        backToRest();
      }}
    >
      <video
        ref={video}
        className={classes.video}
        src={poster === null && thumbnailSeconds === null ? `${src}${FRAME_AT}` : src}
        {...(poster === null ? {} : { poster })}
        preload={poster === null ? "metadata" : "none"}
        muted
        loop
        playsInline
        aria-label={label}
        onLoadedMetadata={(event) => {
          const length = event.currentTarget.duration;
          if (Number.isFinite(length) && length > 0) setReadS(length);
          // With no still, the card shows the rest frame itself, once.
          if (poster === null && thumbnailSeconds !== null && !restedAt.current) {
            restedAt.current = true;
            backToRest();
          }
        }}
        onTimeUpdate={(event) => {
          if (!hovering.current || lengthS === null) return;
          const at = Math.min(lengthS, Math.max(0, event.currentTarget.currentTime));
          setNow(Math.floor(at + SHOWN_PAST_S));
        }}
        onWaiting={() => setBuffering(true)}
        onSeeking={() => {
          if (bufferShown.current !== null) return;
          bufferShown.current = window.setTimeout(() => {
            bufferShown.current = null;
            setBuffering(true);
          }, BUFFER_SHOWN_MS);
        }}
        onSeeked={() => {
          if (bufferShown.current !== null) window.clearTimeout(bufferShown.current);
          bufferShown.current = null;
          setBuffering(false);
        }}
        onError={() => {
          setFailedSrc(src);
          if (readAgain.current) return;
          readAgain.current = true;
          onFailed?.();
        }}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
      />
      {lineAt === null ? null : (
        <span className={classes.line} style={{ left: `${lineAt * 100}%` }} aria-hidden />
      )}
      <span className={classes.time}>
        {buffering ? <Loader size={14} color="var(--cai-color-on-overlay)" aria-label="Loading" /> : null}
        {Icon === null ? null : <Icon size={12} strokeWidth={1.5} aria-hidden />}
        {time.at === null && time.length === null ? null : (
          <span className={classes.range}>
            {time.at === null ? null : <span>{scrubTime(time.at)}</span>}
            {time.at !== null && time.length !== null ? <span>/</span> : null}
            {time.length === null ? null : <span>{scrubTime(time.length)}</span>}
          </span>
        )}
      </span>
    </div>
  );
}
